// EmptyState — componente padrão pra telas/listas vazias.
// Em vez de "Sem dados", explica o que aquela tela faz, quando usar e oferece
// CTA principal pra criar o primeiro registro.

import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@control-center/ui';

import { PillButton } from '../clients/PillButton';

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
  strokeWidth?: number | string;
}

interface EmptyStateProps {
  /** Ícone (lucide-react ou similar) renderizado em círculo grande no topo. */
  icon: ComponentType<IconProps>;
  title: string;
  /** Descrição curta (1-2 linhas) — o que essa tela é. */
  description: string;
  /** Lista opcional de bullets — "quando usar". */
  bullets?: string[];
  /** CTA principal — quando definido, vira botão grande sólido. */
  primaryAction?: {
    label: string;
    /** Rota relativa pra navigate. */
    href?: string;
    onClick?: () => void;
    icon?: ReactNode;
  };
  /** Link secundário — geralmente "Como funciona" ou ajuda. */
  secondaryAction?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  bullets,
  primaryAction,
  secondaryAction,
  className,
}: EmptyStateProps): JSX.Element {
  const navigate = useNavigate();

  function handlePrimary(): void {
    if (primaryAction?.onClick) primaryAction.onClick();
    else if (primaryAction?.href) navigate(primaryAction.href);
  }

  function handleSecondary(): void {
    if (secondaryAction?.onClick) secondaryAction.onClick();
    else if (secondaryAction?.href) navigate(secondaryAction.href);
  }

  return (
    <div
      className={cn(
        'mx-auto flex max-w-[480px] flex-col items-center gap-4 rounded-[14px] border border-line bg-panel-2 px-6 py-10 text-center',
        className,
      )}
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-panel-3 text-muted">
        <Icon size={22} strokeWidth={1.5} />
      </span>
      <div className="flex flex-col gap-1">
        <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
        <p className="text-[13px] text-muted">{description}</p>
      </div>
      {bullets && bullets.length > 0 && (
        <ul className="flex flex-col gap-[6px] text-left text-[12px] text-muted">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <span
                aria-hidden
                className="mt-[6px] h-[3px] w-[3px] shrink-0 rounded-full bg-muted-2"
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {primaryAction && (
            <PillButton solid icon={primaryAction.icon} onClick={handlePrimary}>
              {primaryAction.label}
            </PillButton>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={handleSecondary}
              className="text-[12px] font-medium text-muted hover:text-ink hover:underline"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
